import {
  useUploadAppraisalBlueprintsMutation,
  useUploadAppraisalComparativeImagesMutation,
  useUploadAppraisalFilesMutation,
  useUploadAppraisalImagesMutation,
} from '@endpoints/appraisalsEndpoint'
import {
  useUploadSiteDevelopmentBlueprintsMutation,
  useUploadSiteDevelopmentFilesMutation,
  useUploadSiteDevelopmentImagesMutation,
} from '@endpoints/developmentsEndpoint'
import { useUploadInvestorNewsFilesMutation, useUploadInvestorNewsImagesMutation } from '@endpoints/investorEndpoint'
import { useUploadTaskFilesMutation, useUploadTaskImagesMutation } from '@endpoints/marketingTasksEndpoint'
import { useUploadNewsFilesMutation, useUploadNewsImagesMutation } from '@endpoints/newsEndpoint'
import { useUploadOwnerNewsFilesMutation } from '@endpoints/ownersEndpoint'
import { useUploadPositionImagesMutation } from '@endpoints/positionsEndpoint'
import {
  useUploadPropertyBlueprintsMutation,
  useUploadPropertyFilesMutation,
  useUploadPropertyImagesMutation,
} from '@endpoints/propertiesEndpoint'
import {
  useUploadResourceFilesMutation,
  useUploadResourceImagesMutation,
  useUploadResourceReportFacebookPostImageMutation,
  useUploadResourceReportFacebookStoryImageMutation,
  useUploadResourceReportInstagramInsightImageMutation,
} from '@endpoints/resourcesEndpoint'
import { Id, toast } from 'react-toastify'

export const useGallery = () => {
  interface GalleryData {
    files: File[]
    file_name: string
    text_alt: string
    order?: number
    public_name?: string
    is_active?: boolean
    created_at?: string
  }

  interface GalleryUUIDData {
    file: File
    uuid: string
  }

  interface GalleryArgs {
    id: number
    gallery?: Image[]
    pendingText: string
    successText: string
    errorText: string
    galleryMutation: (args: { id: number; data: GalleryData }) => any
  }
  interface GalleryUUIDArgs {
    id: number
    gallery?: { file: File; uuid: string }[]
    pendingText: string
    successText: string
    errorText: string
    galleryMutation: (args: { id: number; data: GalleryUUIDData }) => any
  }

  interface Image {
    file: File
    text_alt?: string
    order?: number
    public_name?: string
    is_active?: boolean
    created_at?: string
  }

  const galleryFn = async ({ id, gallery, pendingText, successText, errorText, galleryMutation }: GalleryArgs) => {
    if (!gallery || gallery.length === 0) return

    let imagesUploadIndex = 1
    let uploadErrors = 0
    const errorDetails: string[] = []

    const uploadImage = async (image: Image, id: number, toastId: Id) => {
      try {
        const result = await galleryMutation({
          id,
          data: {
            files: [image.file],
            file_name: image.file.name,
            text_alt: image.text_alt ?? '',
            order: image.order,
            public_name: image.public_name,
            is_active: image.is_active,
            created_at: image.created_at,
          },
        }).unwrap()

        return result
      } catch (err: any) {
        uploadErrors++
        const detail =
          err.data?.detail ??
          (Array.isArray(err.data?.file)
            ? err.data.file.join('; ')
            : typeof err.data === 'string'
            ? err.data
            : errorText)

        errorDetails.push(detail)
        throw err
      } finally {
        toast.update(toastId, {
          render: `${pendingText} ${imagesUploadIndex++} de ${gallery.length} ...`,
        })
      }
    }

    const toastId = toast.loading(`${pendingText} 1 de ${gallery.length} ...`)

    const results = await Promise.allSettled(gallery.map((img) => uploadImage(img, id, toastId)))

    const rejected = results.filter((r): r is PromiseRejectedResult => r.status === 'rejected')
    const errorDetailsFinal = rejected.map(
      (r) =>
        (r.reason?.data?.detail as string) ??
        (Array.isArray(r.reason?.data?.file)
          ? r.reason.data.file.join('; ')
          : typeof r.reason?.data === 'string'
          ? r.reason.data
          : errorText),
    )

    const allFailed = uploadErrors === gallery.length
    const partial = uploadErrors > 0 && !allFailed

    const finalMsg = allFailed
      ? errorDetailsFinal.join(' | ')
      : partial
      ? `${successText}: ${gallery.length - uploadErrors} de ${gallery.length}. Errores: ${errorDetailsFinal.join(
          ' | ',
        )}`
      : `${successText}: ${gallery.length} de ${gallery.length}`

    const finalType: 'success' | 'warning' | 'error' = allFailed ? 'error' : partial ? 'warning' : 'success'

    toast.update(toastId, {
      render: finalMsg,
      type: finalType,
      isLoading: false,
      autoClose: 3000,
    })
  }

  const galleryUUIDFn = async ({
    id,
    gallery,
    pendingText,
    successText,
    errorText,
    galleryMutation,
  }: GalleryUUIDArgs) => {
    if (!gallery) return
    let imagesUploadIndex = 1
    let uploadErrors = 0

    const uploadImage = async (image: { file: File; uuid: string }, id: number, toastId: Id) =>
      galleryMutation({
        id,
        data: {
          file: image.file,
          uuid: image.uuid,
        },
      })
        .unwrap()
        .catch(() => uploadErrors++)
        .finally(() => {
          toast.update(toastId, {
            render: `${pendingText} ${imagesUploadIndex++} de ${gallery.length} ...`,
          })
        })

    const toastId = toast.loading(`${pendingText} 1 de ${gallery.length} ...`)

    Promise.allSettled(gallery.map((image) => uploadImage(image, id, toastId)))
      .then(() =>
        toast.update(toastId, {
          render:
            uploadErrors === gallery.length
              ? errorText
              : `${successText}: ${gallery.length - uploadErrors} de ${gallery.length}`,
          type: !uploadErrors ? 'success' : uploadErrors === gallery.length ? 'error' : 'warning',
          isLoading: false,
          autoClose: 3000,
        }),
      )
      .catch(() => {})
  }

  const [uploadPropertyImageMutation] = useUploadPropertyImagesMutation()
  const [uploadPropertyBlueprintMutation] = useUploadPropertyBlueprintsMutation()
  const [uploadPropertyFileMutation] = useUploadPropertyFilesMutation()
  const [uploadDevelopmentImageMutation] = useUploadSiteDevelopmentImagesMutation()
  const [uploadDevelopmentBlueprintMutation] = useUploadSiteDevelopmentBlueprintsMutation()
  const [uploadDevelopmentFileMutation] = useUploadSiteDevelopmentFilesMutation()
  const [uploadNewsImagesMutation] = useUploadNewsImagesMutation()
  const [uploadNewsFilesMutation] = useUploadNewsFilesMutation()
  const [uploadInvestorNewsImagesMutation] = useUploadInvestorNewsImagesMutation()
  const [uploadInvestorNewsFilesMutation] = useUploadInvestorNewsFilesMutation()
  const [uploadAppraisalImageMutation] = useUploadAppraisalImagesMutation()
  const [uploadAppraisalComparativeImageMutation] = useUploadAppraisalComparativeImagesMutation()
  const [uploadAppraisalBlueprintMutation] = useUploadAppraisalBlueprintsMutation()
  const [uploadAppraisalFilesMutation] = useUploadAppraisalFilesMutation()
  const [uploadOwnerNewsFilesMutation] = useUploadOwnerNewsFilesMutation()
  const [uploadResourcesFilesMutation] = useUploadResourceFilesMutation()
  const [uploadResourceReportFacebookPostImageMutation] = useUploadResourceReportFacebookPostImageMutation()
  const [uploadResourceReportFacebookStoryImageMutation] = useUploadResourceReportFacebookStoryImageMutation()
  const [uploadResourceReportInstagramInsightImageMutation] = useUploadResourceReportInstagramInsightImageMutation()
  const [uploadPositionImageMutation] = useUploadPositionImagesMutation()
  const [uploadResourceImageMutation] = useUploadResourceImagesMutation()
  const [uploadMarketingTaskImagesMutation] = useUploadTaskImagesMutation()
  const [uploadMarketingTaskFilesMutation] = useUploadTaskFilesMutation()

  interface UploadArgs {
    id: number
    gallery: Image[]
  }
  interface UploadUUIDArgs {
    id: number
    gallery: { file: File; uuid: string }[]
  }

  const genericImageMessages = {
    pendingText: 'Cargando imagen',
    successText: 'Imagenes cargadas con éxito',
    errorText: 'No se pudieron cargar las imagenes',
  }

  const genericBlueprintMessages = {
    pendingText: 'Cargando plano',
    successText: 'Planos cargados con éxito',
    errorText: 'No se pudieron cargar los planos',
  }

  const genericFileMessages = {
    pendingText: 'Cargando archivo',
    successText: 'Archivos cargados con éxito',
    errorText: 'No se pudieron cargar los archivos',
  }

  const comparativeMessages = {
    pendingText: 'Cargando imagen comparativa',
    successText: 'Imagenes comparativas cargadas con éxito',
    errorText: 'No se pudieron cargar las imagenes comparativas',
  }

  const uploadPropertyImages = async ({ id, gallery }: UploadArgs) =>
    galleryFn({
      id,
      gallery,
      ...genericImageMessages,
      galleryMutation: uploadPropertyImageMutation,
    })

  const uploadPropertyBlueprints = async ({ id, gallery }: UploadArgs) =>
    galleryFn({
      id,
      gallery,
      ...genericBlueprintMessages,
      galleryMutation: uploadPropertyBlueprintMutation,
    })

  const uploadPropertyFiles = async ({ id, gallery }: UploadArgs) =>
    galleryFn({
      id,
      gallery,
      ...genericFileMessages,
      galleryMutation: uploadPropertyFileMutation,
    })

  const uploadDevelopmentImages = async ({ id, gallery }: UploadArgs) =>
    galleryFn({
      id,
      gallery,
      ...genericImageMessages,
      galleryMutation: uploadDevelopmentImageMutation,
    })

  const uploadDevelopmentBlueprints = async ({ id, gallery }: UploadArgs) =>
    galleryFn({
      id,
      gallery,
      ...genericBlueprintMessages,
      galleryMutation: uploadDevelopmentBlueprintMutation,
    })

  const uploadDevelopmentFiles = async ({ id, gallery }: UploadArgs) =>
    galleryFn({
      id,
      gallery,
      ...genericFileMessages,
      galleryMutation: uploadDevelopmentFileMutation,
    })

  const uploadNewsImages = async ({ id, gallery }: UploadArgs) =>
    galleryFn({
      id,
      gallery,
      ...genericImageMessages,
      galleryMutation: uploadNewsImagesMutation,
    })

  const uploadNewsFiles = async ({ id, gallery }: UploadArgs) =>
    galleryFn({
      id,
      gallery,
      ...genericFileMessages,
      galleryMutation: uploadNewsFilesMutation,
    })

  const uploadInvestorNewsImages = async ({ id, gallery }: UploadArgs) =>
    galleryFn({
      id,
      gallery,
      ...genericImageMessages,
      galleryMutation: uploadInvestorNewsImagesMutation,
    })

  const uploadInvestorNewsFiles = async ({ id, gallery }: UploadArgs) =>
    galleryFn({
      id,
      gallery,
      ...genericFileMessages,
      galleryMutation: uploadInvestorNewsFilesMutation,
    })

  const uploadAppraisalImages = async ({ id, gallery }: UploadArgs) =>
    galleryFn({
      id,
      gallery,
      ...genericImageMessages,
      galleryMutation: uploadAppraisalImageMutation,
    })

  const uploadAppraisalComparativeImages = async ({ id, gallery }: UploadArgs) =>
    galleryFn({
      id,
      gallery,
      ...comparativeMessages,
      galleryMutation: uploadAppraisalComparativeImageMutation,
    })

  const uploadAppraisalBlueprints = async ({ id, gallery }: UploadArgs) =>
    galleryFn({
      id,
      gallery,
      ...genericBlueprintMessages,
      galleryMutation: uploadAppraisalBlueprintMutation,
    })

  const uploadAppraisalFiles = async ({ id, gallery }: UploadArgs) =>
    galleryFn({
      id,
      gallery,
      ...genericFileMessages,
      galleryMutation: uploadAppraisalFilesMutation,
    })

  const uploadOwnerNewsFiles = async ({ id, gallery }: UploadArgs) =>
    galleryFn({
      id,
      gallery,
      ...genericFileMessages,
      galleryMutation: uploadOwnerNewsFilesMutation,
    })

  const uploadResourceFiles = async ({ id, gallery }: UploadArgs) =>
    galleryFn({
      id,
      gallery,
      ...genericFileMessages,
      galleryMutation: uploadResourcesFilesMutation,
    })

  const uploadResourceReportFacebookPostImage = async ({ id, gallery }: UploadUUIDArgs) =>
    galleryUUIDFn({
      id,
      gallery,
      pendingText: 'Cargando imagen para informe de Facebook - Posts',
      successText: 'Imagenes para informe de Facebook - Posts cargadas con éxito',
      errorText: 'No se pudieron cargar las imagenes para informe de Facebook - Posts',
      galleryMutation: uploadResourceReportFacebookPostImageMutation,
    })

  const uploadResourceReportFacebookStoryImage = async ({ id, gallery }: UploadUUIDArgs) =>
    galleryUUIDFn({
      id,
      gallery,
      pendingText: 'Cargando imagen para informe de Facebook - Historias',
      successText: 'Imagenes para informe de Facebook - Historias cargadas con éxito',
      errorText: 'No se pudieron cargar las imagenes para informe de Facebook - Historias',
      galleryMutation: uploadResourceReportFacebookStoryImageMutation,
    })

  const uploadResourceReportInstagramInsightImage = async ({ id, gallery }: UploadUUIDArgs) =>
    galleryUUIDFn({
      id,
      gallery,
      pendingText: 'Cargando imagen para informe de Instagram',
      successText: 'Imagenes para informe de Instagram cargadas con éxito',
      errorText: 'No se pudieron cargar las imagenes para informe de Instagram',
      galleryMutation: uploadResourceReportInstagramInsightImageMutation,
    })

  const uploadPositionImages = async ({ id, gallery }: UploadArgs) =>
    galleryFn({
      id,
      gallery,
      ...genericImageMessages,
      galleryMutation: uploadPositionImageMutation,
    })
  const uploadResourceImages = async ({ id, gallery }: UploadArgs) =>
    galleryFn({
      id,
      gallery,
      ...genericImageMessages,
      galleryMutation: uploadResourceImageMutation,
    })
  const uploadMarketingTaskImages = async ({ id, gallery }: UploadArgs) =>
    galleryFn({
      id,
      gallery,
      ...genericImageMessages,
      galleryMutation: uploadMarketingTaskImagesMutation,
    })

  const uploadMarketingTaskFiles = async ({ id, gallery }: UploadArgs) =>
    galleryFn({
      id,
      gallery,
      ...genericFileMessages,
      galleryMutation: uploadMarketingTaskFilesMutation,
    })

  return {
    uploadPropertyImages,
    uploadPropertyBlueprints,
    uploadPropertyFiles,
    uploadDevelopmentImages,
    uploadDevelopmentBlueprints,
    uploadDevelopmentFiles,
    uploadNewsImages,
    uploadNewsFiles,
    uploadInvestorNewsImages,
    uploadInvestorNewsFiles,
    uploadAppraisalImages,
    uploadAppraisalComparativeImages,
    uploadAppraisalBlueprints,
    uploadAppraisalFiles,
    uploadOwnerNewsFiles,
    uploadResourceFiles,
    uploadResourceReportFacebookPostImage,
    uploadResourceReportFacebookStoryImage,
    uploadResourceReportInstagramInsightImage,
    uploadPositionImages,
    uploadResourceImages,
    uploadMarketingTaskImages,
    uploadMarketingTaskFiles,
  }
}
